<script>
import Swiper, { Autoplay } from 'swiper'
import 'swiper/css'

const RECREATE_PROPS = [
    'perPage',
    'perPageCustom',
    'scrollPerPage',
    'minSwipeDistance',
    'autoplay',
    'loop',
    'autoplayTimeout',
    'spacePadding',
    'mouseDrag',
    'resistanceCoef',
]

export default {
    name: 'SharedCarousel',
    props: {
        perPage: {
            type: Number,
            default: 1,
        },
        perPageCustom: {
            type: Array,
            default: () => [],
        },
        paginationEnabled: {
            type: Boolean,
            default: true,
        },
        resistanceCoef: {
            type: Number,
            default: 20,
        },
        scrollPerPage: {
            type: Boolean,
            default: true,
        },
        minSwipeDistance: {
            type: Number,
            default: 8,
        },
        autoplay: {
            type: Boolean,
            default: false,
        },
        loop: {
            type: Boolean,
            default: false,
        },
        autoplayTimeout: {
            type: Number,
            default: 5000,
        },
        spacePadding: {
            type: Number,
            default: 0,
        },
        mouseDrag: {
            type: Boolean,
            default: true,
        },
    },
    data() {
        return {
            swiper: null,
            slideCount: 0,
            currentPage: 0,
            pageCount: 0,
            createFrame: null,
            resizeObserver: null,
            lastContainerWidth: 0,
        }
    },
    computed: {
        normalizedPerPage() {
            return Number(this.perPage) || 1
        },
        breakpointConfig() {
            if (!this.perPageCustom.length) {
                return undefined
            }

            return this.perPageCustom.reduce((config, [width, slidesPerView]) => {
                const normalizedSlides = Number(slidesPerView) || 1

                config[Number(width) || 0] = {
                    slidesPerView: normalizedSlides,
                    slidesPerGroup: this.scrollPerPage ? normalizedSlides : 1,
                }

                return config
            }, {})
        },
        paginationPageCount() {
            return this.loop ? this.slideCount : this.pageCount
        },
    },
    created() {
        RECREATE_PROPS.forEach(key => {
            this.$watch(key, () => this.recreate(), { deep: key === 'perPageCustom' })
        })
    },
    mounted() {
        this.startResizeObserver()
        this.scheduleCreate()
    },
    updated() {
        this.syncSlides()
    },
    beforeDestroy() {
        this.stopResizeObserver()
        this.cancelCreate()
        this.destroySwiper()
    },
    methods: {
        buildOptions(initialPage = 0) {
            const maxPage = Math.max(this.slideCount - 1, 0)
            const initialSlide = Math.max(0, Math.min(initialPage, maxPage))

            return {
                modules: [Autoplay],
                watchOverflow: true,
                updateOnWindowResize: true,
                loop: this.loop && this.slideCount > 1,
                initialSlide,
                autoplay: this.autoplay
                    ? {
                        delay: this.autoplayTimeout,
                        disableOnInteraction: false,
                    }
                    : false,
                slidesPerView: this.normalizedPerPage,
                slidesPerGroup: this.scrollPerPage ? this.normalizedPerPage : 1,
                breakpoints: this.breakpointConfig,
                threshold: this.minSwipeDistance,
                resistance: this.resistanceCoef > 0,
                resistanceRatio: this.getResistanceRatio(),
                simulateTouch: this.mouseDrag,
                allowTouchMove: this.mouseDrag && this.slideCount > 1,
                slidesOffsetBefore: this.spacePadding,
                slidesOffsetAfter: this.spacePadding,
            }
        },
        getResistanceRatio() {
            if (this.resistanceCoef <= 0) {
                return 0
            }

            return Math.max(0.15, Math.min(0.85, 20 / this.resistanceCoef))
        },
        getSlideCount() {
            return (this.$slots.default || []).filter(vnode => {
                if (!vnode || vnode.isComment) {
                    return false
                }

                if (vnode.text) {
                    return vnode.text.trim().length > 0
                }

                return Boolean(vnode.tag || vnode.componentOptions)
            }).length
        },
        scheduleCreate(initialPage = this.currentPage) {
            this.cancelCreate()

            this.createFrame = window.requestAnimationFrame(() => {
                this.createFrame = null
                this.createSwiper(initialPage)
            })
        },
        cancelCreate() {
            if (!this.createFrame) {
                return
            }

            window.cancelAnimationFrame(this.createFrame)
            this.createFrame = null
        },
        createSwiper(initialPage = 0) {
            if (!this.$refs.container || this.swiper) {
                return
            }

            this.slideCount = this.getSlideCount()

            if (this.slideCount === 0) {
                this.emitState()
                return
            }

            const width = this.$refs.container.clientWidth

            if (width === 0) {
                this.scheduleCreate(initialPage)
                return
            }

            this.lastContainerWidth = width
            this.swiper = new Swiper(this.$refs.container, this.buildOptions(initialPage))
            this.swiper.on('slideChange', this.handleSlideChange)
            this.syncState()
        },
        destroySwiper() {
            if (!this.swiper) {
                return
            }

            this.swiper.off('slideChange', this.handleSlideChange)
            this.swiper.destroy(true, false)
            this.swiper = null
        },
        recreate(initialPage = this.currentPage) {
            this.destroySwiper()
            this.scheduleCreate(initialPage)
        },
        syncSlides() {
            const nextSlideCount = this.getSlideCount()

            if (nextSlideCount === this.slideCount) {
                return
            }

            this.slideCount = nextSlideCount

            if (nextSlideCount === 0) {
                this.destroySwiper()
                this.currentPage = 0
                this.pageCount = 0
                this.emitState()
                return
            }

            this.recreate()
        },
        startResizeObserver() {
            if (typeof ResizeObserver === 'undefined' || !this.$refs.container) {
                return
            }

            this.resizeObserver = new ResizeObserver(([entry]) => {
                const width = entry?.contentRect?.width || 0

                if (width === 0 || width === this.lastContainerWidth) {
                    return
                }

                this.lastContainerWidth = width

                if (!this.swiper) {
                    this.scheduleCreate()
                    return
                }

                this.recreate()
            })

            this.resizeObserver.observe(this.$refs.container)
        },
        stopResizeObserver() {
            if (!this.resizeObserver) {
                return
            }

            this.resizeObserver.disconnect()
            this.resizeObserver = null
        },
        syncState() {
            if (!this.swiper) {
                this.currentPage = 0
                this.pageCount = 0
                this.emitState()
                return
            }

            this.slideCount = this.getSlideCount()
            this.currentPage = this.swiper.params.loop ? this.swiper.realIndex : this.swiper.activeIndex
            this.pageCount = this.swiper.params.loop ? this.slideCount : this.swiper.snapGrid.length
            this.emitState()
        },
        emitState() {
            this.$emit('state-change', {
                currentPage: this.currentPage,
                pageCount: this.pageCount,
                isBeginning: this.currentPage === 0,
                isEnd: this.pageCount <= 1 || this.currentPage >= this.pageCount - 1,
            })
        },
        handleSlideChange() {
            this.syncState()
        },
        next() {
            if (!this.swiper || this.swiper.destroyed) {
                return
            }

            this.swiper.slideNext()
        },
        prev() {
            if (!this.swiper || this.swiper.destroyed) {
                return
            }

            this.swiper.slidePrev()
        },
        goToPage(page) {
            if (!this.swiper || this.swiper.destroyed) {
                return
            }

            if (this.swiper.params.loop) {
                this.swiper.slideToLoop(page)
            } else {
                this.swiper.slideTo(page)
            }
        },
    },
}
</script>

<template>
    <div
        v-bind="$attrs"
        class="sharedCarousel"
    >
        <div
            ref="container"
            class="sharedCarouselViewport swiper"
        >
            <div
                ref="wrapper"
                class="swiper-wrapper"
            >
                <slot />
            </div>
        </div>

        <div
            v-if="paginationEnabled && paginationPageCount > 1"
            class="sharedCarouselPagination"
        >
            <div class="sharedCarouselDots">
                <button
                    v-for="page in paginationPageCount"
                    :key="`sharedCarouselDot-${page - 1}`"
                    type="button"
                    :class="[
                        'sharedCarouselDot',
                        { isActive: currentPage === page - 1 },
                    ]"
                    :aria-label="`Go to slide ${page}`"
                    @click="goToPage(page - 1)"
                />
            </div>
        </div>
    </div>
</template>

<style lang="scss" scoped>
.sharedCarousel {
    position: relative;
}

.sharedCarouselViewport {
    overflow: hidden;
}

.sharedCarouselSlide {
    height: auto;
}

.sharedCarouselDots {
    display: inline-flex;
    align-items: center;
}

.sharedCarouselDot {
    display: block;
    appearance: none;
    cursor: pointer;
    background: transparent;
    border: 0;
}
</style>
